// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ActionPayload, ActionType, PendingAction } from "@/lib/pending-actions/types";

// ── Mock data ───────────────────────────────────────────────────────────────
// TODO: Replace with real API calls when backend is ready

const MOCK_ACTIONS: PendingAction[] = [
  {
    id: "a1b2c3d4-0001-4000-8000-000000000001",
    actionType: "create-chain",
    payload: {
      type: "create-chain",
      chainName: "aldi_nord",
      legibleName: "Aldi Nord",
      defaultTimeZone: "Europe/Berlin",
    },
    status: "pending",
    requester: { userId: 12, fullName: "Maria Schmidt", email: "m.schmidt@focal.systems" },
    scheduledFor: null,
    resolvedBy: null,
    resolvedAt: null,
    requesterMessage: "We need this for the Q3 rollout.",
    rejectionReason: null,
    createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
  },
  {
    id: "a1b2c3d4-0001-4000-8000-000000000002",
    actionType: "create-chain",
    payload: {
      type: "create-chain",
      chainName: "rewe_south",
      legibleName: "REWE South",
      defaultTimeZone: "Europe/Berlin",
      copyFrom: "rewe_north",
    },
    status: "pending",
    requester: { userId: 15, fullName: "Jan Kowalski", email: "j.kowalski@focal.systems" },
    scheduledFor: new Date(Date.now() + 1000 * 60 * 60 * 24 * 3).toISOString(),
    resolvedBy: null,
    resolvedAt: null,
    requesterMessage: null,
    rejectionReason: null,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
  },
  {
    id: "a1b2c3d4-0001-4000-8000-000000000003",
    actionType: "create-chain",
    payload: {
      type: "create-chain",
      chainName: "tesco_pl",
      legibleName: "Tesco Poland",
      defaultTimeZone: "Europe/Warsaw",
    },
    status: "approved",
    requester: { userId: 12, fullName: "Maria Schmidt", email: "m.schmidt@focal.systems" },
    scheduledFor: new Date(Date.now() + 1000 * 60 * 60 * 48).toISOString(),
    resolvedBy: { userId: 1, fullName: "Łukasz Milewski" },
    resolvedAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    requesterMessage: null,
    rejectionReason: null,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
  },
  {
    id: "a1b2c3d4-0001-4000-8000-000000000004",
    actionType: "create-chain",
    payload: {
      type: "create-chain",
      chainName: "penny_de",
      legibleName: "Penny DE",
      defaultTimeZone: "Europe/Berlin",
    },
    status: "rejected",
    requester: { userId: 15, fullName: "Jan Kowalski", email: "j.kowalski@focal.systems" },
    scheduledFor: null,
    resolvedBy: { userId: 1, fullName: "Łukasz Milewski" },
    resolvedAt: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
    requesterMessage: null,
    rejectionReason: "Chain naming convention not followed — should use penny_market_de",
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
  },
  {
    id: "a1b2c3d4-0001-4000-8000-000000000005",
    actionType: "create-chain",
    payload: {
      type: "create-chain",
      chainName: "carrefour_it",
      legibleName: "Carrefour Italy",
      defaultTimeZone: "Europe/Rome",
    },
    status: "executed",
    requester: { userId: 12, fullName: "Maria Schmidt", email: "m.schmidt@focal.systems" },
    scheduledFor: null,
    resolvedBy: { userId: 1, fullName: "Łukasz Milewski" },
    resolvedAt: new Date(Date.now() - 1000 * 60 * 60 * 36).toISOString(),
    requesterMessage: null,
    rejectionReason: null,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
  },
];

let mockActions = [...MOCK_ACTIONS];

function resetMockActions() {
  mockActions = [...MOCK_ACTIONS];
}

// ── Query ───────────────────────────────────────────────────────────────────

export function usePendingActionsQuery() {
  return useQuery({
    queryKey: ["pending-actions"],
    queryFn: async () => {
      // TODO: Replace with api.GET("/v1/pending-actions")
      await new Promise((r) => setTimeout(r, 100)); // Simulate network
      const pendingCount = mockActions.filter((a) => a.status === "pending").length;
      return { actions: mockActions, count: mockActions.length, pendingCount };
    },
  });
}

// ── Submit mutation ─────────────────────────────────────────────────────────

interface SubmitPendingActionParams {
  actionType: ActionType;
  payload: ActionPayload;
  scheduledFor?: string;
  message?: string;
}

export function useSubmitPendingActionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: SubmitPendingActionParams) => {
      // TODO: Replace with api.POST("/v1/pending-actions", { body: params })
      await new Promise((r) => setTimeout(r, 200)); // Simulate network
      const newAction: PendingAction = {
        id: crypto.randomUUID(),
        actionType: params.actionType,
        payload: params.payload,
        status: "pending",
        requester: { userId: 0, fullName: "You", email: "you@focal.systems" },
        scheduledFor: params.scheduledFor ?? null,
        resolvedBy: null,
        resolvedAt: null,
        requesterMessage: params.message ?? null,
        rejectionReason: null,
        createdAt: new Date().toISOString(),
      };
      mockActions = [newAction, ...mockActions];
      return { status: "ok" as const, id: newAction.id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-actions"] });
    },
  });
}

// ── Resolve mutation (approve/reject) ───────────────────────────────────────

interface ResolvePendingActionParams {
  id: string;
  status: "approved" | "rejected";
  rejectionReason?: string;
}

export function useResolvePendingActionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: ResolvePendingActionParams) => {
      // TODO: Replace with api.PATCH("/v1/pending-actions/{id}", { body })
      await new Promise((r) => setTimeout(r, 200)); // Simulate network
      mockActions = mockActions.map((a) => {
        if (a.id !== params.id) return a;
        const resolved: PendingAction = {
          ...a,
          status: params.status === "approved" ? "approved" : "rejected",
          resolvedBy: { userId: 1, fullName: "You (Admin)" },
          resolvedAt: new Date().toISOString(),
          rejectionReason: params.rejectionReason ?? null,
        };
        // Simulate backend executing approved actions without scheduledFor
        if (params.status === "approved" && !a.scheduledFor) {
          resolved.status = "executed";
        }
        return resolved;
      });
      return { status: "ok" as const };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-actions"] });
    },
  });
}

export { resetMockActions };
